import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildServiceListSchema,
  buildSpeakableSchema,
} from "@/lib/schema";
import {
  NAATI_KEYWORDS_TOTAL,
  NAATI_FAQ_TH_TOTAL,
  NAATI_FAQ_EN_TOTAL,
} from "@/content/naati-corpus/counts.gen";
import { NAATI_FAQ_CATEGORIES } from "@/content/naati-corpus/faq-defs";
import { NAATI_VISA_META, NAATI_DOC_META, NAATI_PROVINCE_META } from "@/content/naati-corpus/meta.gen";

export const Route = createFileRoute("/naati/")({
  component: NaatiHub,
  loader: async () => {
    const { NAATI_KEYWORDS_TH_TOP, NAATI_FAQ_TH_TOP } = await import("@/content/naati-corpus/top.gen");
    return {
      keywords: NAATI_KEYWORDS_TH_TOP.slice(0, 120),
      faqs: NAATI_FAQ_TH_TOP.slice(0, 10),
    };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const title = "NAATI แปลรับรอง ประเทศไทย 2026 — ยื่นวีซ่าออสเตรเลีย/นิวซีแลนด์";
    const description = `ศูนย์รวมบริการแปลเอกสาร NAATI Certified สำหรับวีซ่าออสเตรเลีย/นิวซีแลนด์ — Partner, Student, Skilled, Tourist, PR — ครอบคลุม ${NAATI_KEYWORDS_TOTAL}+ คำค้น พร้อม FAQ ${NAATI_FAQ_TH_TOTAL + NAATI_FAQ_EN_TOTAL}+ ข้อ อัปเดต 2026`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/naati",
        type: "website",
        alternates: [{ lang: "en", path: "/en/naati" }],
        keywords: (loaderData?.keywords ?? []).slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "NAATI Translation", url: `${site}/naati` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildServiceListSchema([
              { name: "NAATI Certified Translation (Thai ↔ English)", description: "แปลรับรองโดยนักแปล NAATI สำหรับยื่น DHA / Immigration NZ / IRCC", url: "/services/naati" },
              { name: "NAATI Online / Digital Stamp", description: "แปล NAATI ออนไลน์ ส่งไฟล์ PDF พร้อมตราประทับดิจิทัล", url: "/services/naati-online" },
              { name: "Certified Translation (All)", description: "แปลรับรองทนาย/MOJ/สถานทูต ครบทุกภาษา", url: "/services/translation-certified-all" },
              { name: "Australia Visa Translation Package", description: "แพ็คเกจแปล NAATI + MFA + สถานทูต สำหรับ Partner/Student/Skilled", url: "/services/translation-visa" },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/naati")) },
      ],
    };
  },
});

function NaatiHub() {
  const { keywords, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>NAATI Translation</span>
        </nav>

        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            NAATI แปลรับรอง ประเทศไทย 2026 — คู่มือฉบับสมบูรณ์
          </h1>
          <p className="text-muted-foreground text-lg">
            บริการแปลเอกสารรับรอง NAATI (National Accreditation Authority for Translators and Interpreters) โดยนักแปล
            ที่ได้รับ CPN ถูกต้อง — ยอมรับโดย DHA ออสเตรเลีย, Immigration NZ, IRCC แคนาดา และ UK Home Office
            ครอบคลุมเอกสารทะเบียนบ้าน, สูติบัตร, ทะเบียนสมรส, ใบหย่า, ทรานสคริปต์, ใบขับขี่, PCC และอื่นๆ
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <a href="/services/naati" className="rounded-md bg-primary px-4 py-2 text-primary-foreground font-medium">ดูบริการหลัก →</a>
            <a href="/contact" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">ปรึกษาฟรี</a>
            <a href="/en/naati" className="rounded-md border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">English version →</a>
          </div>
        </header>

        <section aria-label="Related services" className="mb-10">
          <h2 className="text-xl font-bold mb-3">บริการที่เกี่ยวข้อง</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { slug: "naati", title: "NAATI Certified Translation", desc: "แปลรับรอง Thai ↔ English โดยนักแปลที่ได้รับ CPN" },
              { slug: "naati-online", title: "NAATI Online / Digital Stamp", desc: "ส่งไฟล์ PDF พร้อมตราประทับดิจิทัล — 1-2 วัน" },
              { slug: "translation-visa", title: "แพ็คเกจแปลวีซ่า", desc: "NAATI + MFA + สถานทูต สำหรับ Partner/Student/Skilled Visa" },
              { slug: "translation-certified-all", title: "แปลรับรองทุกภาษา", desc: "รวมทนาย / MOJ / สถานทูต / NAATI ในที่เดียว" },
              { slug: "visa-australia-600", title: "วีซ่าท่องเที่ยวออสเตรเลีย 600", desc: "รวม NAATI + สปอนเซอร์ + Statement of Purpose" },
              { slug: "police-clearance", title: "Police Clearance (PCC)", desc: "ขอ PCC + แปล NAATI เพื่อยื่นวีซ่า 12 เดือน+" },
            ].map((s) => (
              <a key={s.slug} href={`/services/${s.slug}`} className="rounded-lg border bg-card p-4 hover:border-primary transition">
                <div className="font-semibold mb-1">{s.title}</div>
                <div className="text-sm text-muted-foreground">{s.desc}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Deep-dive insight" className="mb-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="text-xl font-bold mb-2">คู่มือเจาะลึก NAATI 2026</h2>
          <p className="text-sm text-muted-foreground mb-3">
            อ่านคู่มือ NAATI Translation ประเทศไทย ครบวงจร — ขั้นตอน, ราคา, timeline, sequence กับ MFA, และ 100-point ID rule
          </p>
          <a href="/en/insights/naati-certified-translation-thailand-australia-visa" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium text-sm">
            อ่านคู่มือฉบับเต็ม →
          </a>
        </section>

        <section aria-label="Top Thai questions" className="mb-10">
          <h2 className="text-xl font-bold mb-3">หมวดคำถาม NAATI ทั้งหมด</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            {NAATI_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/naati/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleTh} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descTh}</div>
              </a>
            ))}
          </div>
          <p className="mb-6 text-sm">
            <a href="/naati/faq" className="text-primary underline">
              ดูคำถามที่พบบ่อยทั้งหมด ({NAATI_FAQ_TH_TOTAL} ข้อ) →
            </a>
          </p>
          <h2 className="text-xl font-bold mb-3">คำถามยอดฮิต 10 อันดับ (ภาษาไทย)</h2>
          <div className="space-y-3">
            {faqs.map((item, i) => (
              <details key={i} className="rounded-lg border bg-card p-4">
                <summary className="cursor-pointer font-semibold text-sm">
                  <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
                </summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section aria-label="NAATI by visa subclass" className="mb-10">
          <h2 className="text-xl font-bold mb-3">แปลเอกสาร NAATI แยกตามประเภทวีซ่า</h2>
          <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            {NAATI_VISA_META.map((v) => (
              <a
                key={v.slug}
                href={`/naati/visa/${v.slug}`}
                className="rounded-lg border bg-card px-3 py-2 transition-colors hover:border-primary hover:bg-primary/5"
              >
                {v.nameTh} →
              </a>
            ))}
          </div>
        </section>

        <section aria-label="NAATI by document type" className="mb-10">
          <h2 className="text-xl font-bold mb-3">แปลเอกสาร NAATI แยกตามประเภทเอกสาร</h2>
          <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            {NAATI_DOC_META.map((d) => (
              <a
                key={d.slug}
                href={`/naati/document/${d.slug}`}
                className="rounded-lg border bg-card px-3 py-2 transition-colors hover:border-primary hover:bg-primary/5"
              >
                {d.titleTh} →
              </a>
            ))}
          </div>
        </section>

        <section aria-label="NAATI by province" className="mb-10">
          <h2 className="text-xl font-bold mb-3">แปลเอกสาร NAATI แยกตามจังหวัด</h2>
          <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-3">
            {NAATI_PROVINCE_META.map((p) => (
              <a
                key={p.slug}
                href={`/naati/province/${p.slug}`}
                className="rounded-lg border bg-card px-3 py-2 transition-colors hover:border-primary hover:bg-primary/5"
              >
                NAATI {p.provinceTh} →
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Keyword universe" className="mb-10">
          <h2 className="text-xl font-bold mb-3">คำค้นที่ครอบคลุม ({NAATI_KEYWORDS_TOTAL} คำ)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {keywords.map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">พร้อมยื่นวีซ่าออสเตรเลียใน 3-5 วัน</h2>
          <p className="text-sm text-muted-foreground mb-4">
            ปรึกษาฟรีกับทีม NAATI + ทนายผู้รับมอบอำนาจ — จ.-ส. 9:00-18:00 ICT
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">ติดต่อทีมงาน →</a>
        </section>
        <PageGallery category="translation" seed="naati-hub" locale="th" limit={4} />
        <FaqSourceNote topic="translation" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}